import React, { useContext, useEffect, useState, useRef } from 'react';
import { ShopContext } from '../context/shopcontext';
import ProductItem from './ProductItem';
import BestSeller from './BestSeller';
import axios from 'axios';

const LatestCollections = () => {
  const { backendUrl } = useContext(ShopContext);
  const [latestProducts, setLatestProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);
  const carouselRef = useRef(null);
  const nextSectionRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const isProductInStock = (product) => {
    const noSizes = !product.sizes || product.sizes.length === 0;
    const withSizes = product.sizes && product.sizes.some((s) => s.quantity > 0);
    return (noSizes && product.stockQuantity > 0) || withSizes;
  };

  useEffect(() => {
    const fetchLatest = async () => {
      const cached = sessionStorage.getItem('latestProducts');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const filtered = parsed.filter(
            (product) => product.category !== 'Home Decor' && isProductInStock(product)
          );
          setLatestProducts(filtered);
          return;
        } catch {
          sessionStorage.removeItem('latestProducts');
        }
      }

      try {
        const res = await axios.get(
          `${backendUrl}/api/product/list?sortField=date&sortOrder=-1&limit=20`
        );
        if (res.data.success) {
          const filtered = res.data.products
            .filter((product) => product.category !== 'Home Decor' && isProductInStock(product))
            .slice(0, 10);

          setLatestProducts(filtered);
          sessionStorage.setItem('latestProducts', JSON.stringify(filtered));
        }
      } catch (err) {
        console.error('Failed to fetch latest products:', err);
      }
    };

    fetchLatest();
  }, [backendUrl]);

  useEffect(() => {
    const updateVisibleCount = () => {
      const width = window.innerWidth;
      if (width < 640) setVisibleCount(2);
      else if (width < 768) setVisibleCount(3);
      else setVisibleCount(4);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50;

    if (diff > threshold) nextSlide();
    else if (diff < -threshold) prevSlide();

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const nextSlide = () => {
    if (currentIndex < latestProducts.length - visibleCount) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const scrollToNext = () => {
    if (nextSectionRef.current) {
      nextSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="px-4 pt-6 pb-12 relative">
        <div className="text-center mb-14">
          <h2 className="relative inline-block text-2xl sm:text-4xl md:text-5xl font-cinzel text-[#4c0f2e] tracking-widest mb-6 ">
            Latest Collections
            <span className="absolute left-1/2 transform -translate-x-1/2 -bottom-2 h-0.5 w-14 bg-[#2d161e] rounded-full"></span>
          </h2>
          <p className="text-sm sm:text-base text-[#b09287] max-w-2xl mx-auto leading-relaxed italic">
            Unveiling our latest pieces — timeless expressions of artistry and grace, crafted to
            complement every moment.
          </p>
        </div>

        <div className="relative overflow-hidden ">
          <button
            onClick={prevSlide}
            disabled={currentIndex === 0}
            className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-[#ebe2db] shadow-sm border border-[#b09287] text-[#4c0f2e] w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              currentIndex === 0
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#4c0f2e] hover:text-[#ebe2db]'
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            disabled={currentIndex >= latestProducts.length - visibleCount}
            className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-[#ebe2db] shadow-sm border border-[#b09287] text-[#4c0f2e] w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              currentIndex >= latestProducts.length - visibleCount
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#4c0f2e] hover:text-[#ebe2db]'
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div
            className="flex gap-1 transition-transform duration-300 ease-out cursor-grab active:cursor-grabbing p-2 "
            style={{ transform: `translateX(-${(100 / visibleCount) * currentIndex}%)` }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {latestProducts.map((item, index) => (
              <div
                key={index}
                className="flex-shrink-0"
                style={{ width: `${100 / 2}%`, maxWidth: '300px', height: 'auto' }}
              >
                <ProductItem
                  id={item._id}
                  image={item.image}
                  name={item.name}
                  price={item.price}
                  rating={item.averageRating || 0}
                  totalReviews={item.totalReviews}
                  bestseller={item.bestseller}
                  inStock={item.inStock}
                  category={item.category}
                  subCategory={item.subCategory}
                  sizes={item.sizes}
                  stockQuantity={item.stockQuantity}
                  currentPage={1}
                  categoryFilter={[]}
                  subCategoryFilter={[]}
                  sortType={'relevant'}
                  pageKey={'home'}
                  restock={item.restock}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-6">
            {Array.from({ length: latestProducts.length - visibleCount + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`mx-1 h-2.5 w-2.5 rounded-full transition-all duration-200 ${
                  i === currentIndex ? 'bg-[#4c0f2e]' : 'bg-[#b09287]/40'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="absolute bottom-0 left-1/2 translate-y-1/2 -translate-x-1/2 z-30">
          <button
            onClick={scrollToNext}
            className="w-10 h-10 bg-[#4c0f2e] rounded-full flex items-center justify-center shadow-sm border border-[#b09287]"
            aria-label="Scroll down"
          >
            <svg
              className="w-5 h-5 text-[#ebe2db]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      <div ref={nextSectionRef}>
        <BestSeller />
      </div>
    </>
  );
};

export default LatestCollections;
